"use client"

import * as React from "react"

import { FeatureFlag } from "@ibada/shared-types/feature-flags"

import {
  useFormBranchContext,
  useModulePermission,
  useShowMultiBranchWorkspaceUi,
  useTenantAuthorization,
} from "@/features/auth"
import { WorkspaceAccessModule } from "@/features/auth/model/auth-types"
import {
  TenantAuthorizationAction,
  TenantAuthorizationDecisionStatus,
} from "@/features/auth/tenant-authorization/model/tenant-authorization.types"
import {
  CREATE_KIND_META,
  type CreateAccessFacts,
  type CreateKind,
} from "@/features/create-overlay/model/create-kind"
import { useFeatureFlag } from "@/shared/feature-flags"

export type CreateKindAccess = {
  /** True while the user's access is still loading: decide nothing yet (a direct link must not lose its param). */
  resolving: boolean
  isAllowed: (kind: CreateKind) => boolean
}

/**
 * Who may create what, from the same checks the header "Créer" menu always used (module permissions, the
 * ministries action, the parish context). The menu, the shortcuts and the overlay host all read this, so a kind
 * hidden from the menu can never be opened by typing its param.
 */
export function useCreateKindAccess(): CreateKindAccess {
  const authorization = useTenantAuthorization()
  const { isParishContext } = useFormBranchContext()
  const members = useModulePermission(WorkspaceAccessModule.MEMBERS)
  const canEditMinistries = authorization.can(TenantAuthorizationAction.MinistriesEdit)
  const activities = useModulePermission(WorkspaceAccessModule.ACTIVITIES)
  const assets = useModulePermission(WorkspaceAccessModule.ASSETS)
  const assetsEnabled = useFeatureFlag(FeatureFlag.Assets)
  const canCreateAssets = assetsEnabled && assets.canEdit
  const finance = useModulePermission(WorkspaceAccessModule.FINANCE)
  const budgeting = useModulePermission(WorkspaceAccessModule.BUDGETING)
  const canCreateTransactions = finance.canEdit && authorization.can(TenantAuthorizationAction.TransactionCreate)
  const canEditBudgets = budgeting.canEdit && authorization.can(TenantAuthorizationAction.BudgetingEdit)
  const pledgesEnabled = useFeatureFlag(FeatureFlag.Pledges)
  const budgetingEnabled = useFeatureFlag(FeatureFlag.Budgeting)
  const isMultiBranchWorkspace = useShowMultiBranchWorkspaceUi()
  const resolving =
    authorization.routeDecision(TenantAuthorizationAction.MembersView).status === TenantAuthorizationDecisionStatus.Resolving

  return React.useMemo(() => {
    const facts: CreateAccessFacts = {
      canEditMembers: members.canEdit,
      canEditMinistries,
      isParishContext,
      canEditActivities: activities.canEdit,
      canCreateAssets,
      canCreateTransactions,
      canEditBudgets,
      pledgesEnabled,
      budgetingEnabled,
      isMultiBranchWorkspace,
    }
    return { resolving, isAllowed: (kind: CreateKind) => CREATE_KIND_META[kind].isAllowed(facts) }
  }, [
    activities.canEdit,
    budgetingEnabled,
    canCreateAssets,
    canCreateTransactions,
    canEditBudgets,
    canEditMinistries,
    isMultiBranchWorkspace,
    isParishContext,
    members.canEdit,
    pledgesEnabled,
    resolving,
  ])
}
